import { useRef } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AccountActionDialog, type AccountTarget } from "./AccountActionDialog";
import { createSubmitLock } from "./action-model";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ApiError, unlinkPlayerIdentity, type UnlinkProvider } from "@/lib/api";

export function UnlinkIdentityDialog({ target, provider, onClose }: {
  target: AccountTarget; provider: UnlinkProvider; onClose: () => void;
}) {
  const submitting = useRef(createSubmitLock());
  const qc = useQueryClient();
  const label = provider === "password" ? "email/password" : provider;
  const mutation = useMutation({
    mutationFn: (id: string) => unlinkPlayerIdentity(id, provider),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["pf-account", target.id] });
      void qc.invalidateQueries({ queryKey: ["player-bans", target.id] });
      void qc.invalidateQueries({ queryKey: ["audit"] });
    },
    onSettled: () => {
      submitting.current.end();
      void qc.invalidateQueries({ queryKey: ["player-identities", target.id] });
    },
  });
  const error = mutation.error instanceof ApiError && mutation.error.message === "last_identity"
    ? "Cannot unlink the only remaining login method. Connect another method first."
    : mutation.error instanceof ApiError && mutation.error.message === "not_linked"
      ? "This login method is already unlinked." : String(mutation.error);

  return <AccountActionDialog title={`Unlink ${label}`} scope="Removes this login method from the selected player."
    target={target} pending={mutation.isPending} changed={false} onClose={onClose}>
    <p className="text-sm">Unlink {label}? The player will need another connected method to sign in.
      Sessions opened through {label} will be signed out.</p>
    <Button variant="destructive" disabled={mutation.isPending || mutation.isSuccess || !!mutation.error}
      onClick={() => {
        if (!submitting.current.begin()) return;
        mutation.mutate(target.id);
      }}>{mutation.isPending ? "Unlinking…" : `Unlink ${label}`}</Button>
    {mutation.error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}
    {mutation.isSuccess && <Alert><AlertDescription>{label} unlinked.</AlertDescription></Alert>}
  </AccountActionDialog>;
}
